import { Card, CardContent } from "@patient-watch/ui/card";
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "@patient-watch/ui/form";
import { Label } from "@patient-watch/ui/label";
import { cn } from "@/lib/utils";
import { useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";

export interface BranchingScaleFormValue {
    branch: string;
    value: number;
}

interface BranchOption {
    label: string;
    value: string;
    text: string;
    durationPrompt?: string;
}

interface VisaQ8BaseProps {
    name: string;
    required?: boolean;
    questionLabel: string;
    branches: BranchOption[];
    scaleLabelsMap: Record<string, string[]>;
    pointsMap: Record<string, number[]>;
    durationPrompt: string;
}

function getBranchFromFieldValue(fieldValue: unknown): string | null {
    if (fieldValue && typeof fieldValue === "object" && "branch" in fieldValue) {
        const branch = (fieldValue as BranchingScaleFormValue).branch;
        return typeof branch === "string" ? branch : null;
    }
    return null;
}

function getPointsFromFieldValue(fieldValue: unknown): number | null {
    if (typeof fieldValue === "number" && Number.isFinite(fieldValue)) {
        return fieldValue;
    }
    if (fieldValue && typeof fieldValue === "object" && "value" in fieldValue) {
        const value = Number((fieldValue as BranchingScaleFormValue).value);
        return Number.isFinite(value) ? value : null;
    }
    return null;
}

const VisaQ8Base = ({
    name,
    required = false,
    questionLabel,
    branches,
    scaleLabelsMap,
    pointsMap,
    durationPrompt,
}: VisaQ8BaseProps) => {
    const { control, watch } = useFormContext();
    const fieldValue = watch(name);
    const [selectedBranch, setSelectedBranch] = useState<string | null>(() => getBranchFromFieldValue(fieldValue));

    useEffect(() => {
        const branchFromValue = getBranchFromFieldValue(fieldValue);
        if (branchFromValue && branchFromValue !== selectedBranch) {
            setSelectedBranch(branchFromValue);
        }
    }, [fieldValue, selectedBranch]);

    return (
        <Card>
            <CardContent className="p-4">
                <FormField
                    control={control}
                    name={name}
                    render={({ field }) => {
                        const selectedPoints = getPointsFromFieldValue(field.value);

                        return (
                            <FormItem className="space-y-4">
                                <FormLabel>
                                    <Label>
                                        {questionLabel}
                                        {required && <span className="text-red-500 ml-0.5">*</span>}
                                    </Label>
                                </FormLabel>
                                <FormControl>
                                    <div className="space-y-3" role="radiogroup" aria-label={name}>
                                        {branches.map((branch) => {
                                            const isActive = selectedBranch === branch.value;
                                            const branchDurationPrompt = branch.durationPrompt || durationPrompt;
                                            const labels = scaleLabelsMap[branch.value] ?? [];
                                            const points = pointsMap[branch.value] ?? [];

                                            return (
                                                <div key={branch.value} className="flex flex-col">
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            setSelectedBranch(branch.value);
                                                            field.onChange(null);
                                                        }}
                                                        className={cn(
                                                            "flex items-center gap-3 p-3 rounded-lg w-full transition-all text-left",
                                                            "hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-indigo-500",
                                                            isActive && "bg-indigo-50"
                                                        )}
                                                    >
                                                        <div
                                                            className={cn(
                                                                "flex justify-center items-center shrink-0 rounded-full w-10 h-10 font-medium text-lg transition-colors",
                                                                isActive
                                                                    ? "bg-indigo-600 text-white"
                                                                    : "bg-white border-2 border-slate-300 text-slate-700"
                                                            )}
                                                        >
                                                            {branch.label}
                                                        </div>
                                                        <span className="text-slate-700 text-sm">{branch.text}</span>
                                                    </button>

                                                    {isActive && (
                                                        <div className="ml-[52px] mt-2 mb-2 space-y-2">
                                                            <span className="text-slate-500 text-xs font-medium uppercase tracking-wider">
                                                                {branchDurationPrompt}
                                                            </span>
                                                            <div className="space-y-1">
                                                                {labels.map((label, index) => {
                                                                    const optionPoints = points[index];
                                                                    if (optionPoints == null) return null;
                                                                    const isSelected = selectedPoints === optionPoints;
                                                                    return (
                                                                        <button
                                                                            type="button"
                                                                            key={`${branch.value}-${index}`}
                                                                            onClick={() =>
                                                                                field.onChange({
                                                                                    branch: branch.value,
                                                                                    value: optionPoints,
                                                                                } satisfies BranchingScaleFormValue)
                                                                            }
                                                                            className={cn(
                                                                                "flex items-center gap-3 p-3 rounded-lg w-full transition-all text-left",
                                                                                "hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-indigo-500",
                                                                                isSelected && "bg-indigo-50"
                                                                            )}
                                                                        >
                                                                            <div
                                                                                className={cn(
                                                                                    "flex justify-center items-center shrink-0 rounded-full w-10 h-10 font-medium text-lg transition-colors",
                                                                                    isSelected
                                                                                        ? "bg-indigo-600 text-white"
                                                                                        : "bg-white border-2 border-slate-300 text-slate-700"
                                                                                )}
                                                                            >
                                                                                {optionPoints}
                                                                            </div>
                                                                            <span className="text-slate-700 text-sm">
                                                                                {label}
                                                                            </span>
                                                                        </button>
                                                                    );
                                                                })}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        );
                    }}
                />
            </CardContent>
        </Card>
    );
};

export default VisaQ8Base;
